/**
 * PqcAuditSuite — bundle configurator for the 3 moat-deepener PQC audits.
 *
 * Lives at the top of `/pricing/audits`, above the standard tiers grid.
 * Renders 3 toggleable audit cards (Hybrid Signature, EIP-7702 / AA,
 * MPC Wallet) with a live-updating total + stacked-volume discount:
 *
 *     1 audit  →  no discount (full price)
 *     2 audits →  15% off the subtotal
 *     3 audits →  25% off the subtotal — "Full Suite"
 *
 * Prices + discount math are computed by the backend
 * (`/api/public/pricing/audits/bundle-quote`) so the source of truth
 * stays in `pricing_bundle.py`. The frontend just renders the quote.
 *
 * Conversion: "Get suite quote" → `/audit/talk-to-sales?intent=pqc-suite&...`
 * preserving the selection + tier so the sales-intake form opens
 * pre-filled.
 */
import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Check, ArrowRight, Sparkles, Atom, Cpu, Network } from "lucide-react";
import { useUserAuth } from "../../lib/userAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = { atom: Atom, cpu: Cpu, network: Network };

/**
 * Iter272 — Map the signed-in account's plan onto the configurator's
 * subscription chooser. Returns "builder" / "team" / "enterprise" /
 * "none" so the auto-select effect can pre-pick the chip without
 * extra catalog calls.
 *
 *   account.plan === "team"               → "team"        (+15% + Matrix Pro)
 *   account.plan === "pro"                → "builder"     (+10%)
 *   account.enterprise_track === true     → "enterprise"  (all-inclusive)
 *   anything else / no account            → "none"
 */
function accountToSubscriptionTier(account) {
  if (!account) return "none";
  if (account.enterprise_track === true) return "enterprise";
  const plan = (account.plan || "").toLowerCase();
  if (plan === "team") return "team";
  if (plan === "pro")  return "builder";
  return "none";
}

export default function PqcAuditSuite() {
  const [catalog, setCatalog] = useState(null);
  const [tier, setTier] = useState("standard");          // standard | deep_dive
  const [selectedSlugs, setSelectedSlugs] = useState([]); // [slug, ...]
  // Iter270 — subscription unlock chooser. "none" = no subscription
  // assumed; "builder" = +10%; "team" = +15% + Matrix Pro included;
  // "enterprise" = all-inclusive (quoted separately).
  const [subscription, setSubscription] = useState("none");
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch the bundle catalog once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/public/pricing/audits/bundle`);
        if (cancelled) return;
        setCatalog(r.data);
        // Default: pre-select all 3 to surface the headline 25% discount.
        setSelectedSlugs(r.data.audits.map((a) => a.slug));
      } catch {
        if (!cancelled) setCatalog(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Re-fetch the quote whenever (selection, tier, subscription) changes.
  useEffect(() => {
    if (!selectedSlugs.length) { setQuote(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/public/pricing/audits/bundle-quote`,
          { params: { slugs: selectedSlugs.join(","), tier, subscription } },
        );
        if (!cancelled) setQuote(r.data);
      } catch {
        if (!cancelled) setQuote(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedSlugs, tier, subscription]);

  const toggleSlug = (slug) => {
    setSelectedSlugs((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug],
    );
  };

  // Sales-handoff fallback URL — used for anonymous visitors, Enterprise
  // all-inclusive customers (no checkout needed), and as the graceful
  // failure path when Stripe is unreachable.
  const quoteUrl = useMemo(() => {
    const params = new URLSearchParams();
    params.set("intent", "pqc-suite");
    params.set("tier", tier);
    if (selectedSlugs.length) params.set("audits", selectedSlugs.join(","));
    if (subscription !== "none") params.set("subscription", subscription);
    if (quote?.total_usd) params.set("estimate_usd", String(quote.total_usd));
    return `/audit/talk-to-sales?${params.toString()}`;
  }, [tier, selectedSlugs, subscription, quote?.total_usd]);

  // Iter274 — Multi-line Stripe Checkout for signed-in buyers. Posts to
  // /api/billing/bundle-checkout, which validates the subscription claim
  // server-side, creates the Stripe Session + Coupon, and returns the
  // hosted-checkout URL.
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError]   = useState(null);
  const handleSuiteCheckout = async (e) => {
    // Anonymous users go to sales; Enterprise all-inclusive too.
    if (!account || quote?.all_inclusive) return;  // <Link> fallback handles them
    e.preventDefault();
    setCheckoutError(null);
    setCheckoutLoading(true);
    try {
      const resp = await axios.post(
        `${API}/billing/bundle-checkout`,
        {
          slugs: selectedSlugs,
          tier, subscription,
          origin_url: window.location.origin,
        },
        { withCredentials: true },
      );
      if (resp.data?.url) {
        window.location.assign(resp.data.url);
        return;
      }
      throw new Error("missing checkout url");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && detail?.error === "all_inclusive_subscription") {
        // Enterprise · Atlas — gracefully degrade to a sales handoff
        // with a sticky message instead of a 5xx.
        setCheckoutError(detail.message);
      } else if (status === 401) {
        // Session expired between page-load and click — fall back to the
        // sales-handoff link (default <Link> click target).
        window.location.assign(quoteUrl);
        return;
      } else {
        setCheckoutError(
          "Couldn't open Stripe checkout — please try again, or "
          + "contact sales.",
        );
      }
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (loading || !catalog) return null;

  return (
    <section className="max-w-[1400px] mx-auto px-6 sm:px-8 pt-4 pb-10"
             data-testid="pqc-audit-suite">
      <div className="qt-card relative overflow-hidden"
           style={{
             border: "1px solid rgba(31,221,131,0.40)",
             background:
               "radial-gradient(ellipse at top right, rgba(31,221,131,0.10), transparent 60%), " +
               "linear-gradient(135deg, rgba(0,229,255,0.05), transparent 60%)",
             padding: "1.75rem 1.75rem 1.5rem",
           }}>
        {/* Header strip */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="qt-mono text-xs"
                 style={{ color: "#1FDD83", letterSpacing: "0.20em", fontWeight: 700 }}
                 data-testid="pqc-suite-eyebrow">
              <Sparkles size={12} className="inline mr-1.5" />
              QORTRACE PQC AUDIT SUITE · MOAT-DEEPENER LINE
            </div>
            <h2 className="qt-display text-3xl sm:text-4xl mt-2"
                style={{ color: "#fff", lineHeight: 1.1 }}
                data-testid="pqc-suite-headline">
              Bundle the only 3 PQC-specialist audits{" "}
              <span style={{ color: "#1FDD83" }}>in the world</span>.
            </h2>
            <p className="mt-3 max-w-2xl"
               style={{ color: "var(--qt-text-2)", lineHeight: 1.6 }}>
              Save 15% on any two, 25% on all three. Single signed
              engagement letter, single delivery window, one set of
              warm-handoff intros to NIST + ETSI + IETF working
              groups when you need them.
            </p>
          </div>

          {/* Tier toggle */}
          <div className="inline-flex items-center gap-1 rounded-full p-1"
               style={{ background: "rgba(255,255,255,0.04)", border: "1px solid var(--qt-border)" }}
               data-testid="pqc-suite-tier-toggle">
            {["standard", "deep_dive"].map((t) => (
              <button key={t} type="button"
                      data-testid={`pqc-suite-tier-${t}`}
                      onClick={() => setTier(t)}
                      className="qt-mono text-xs px-4 py-1.5 rounded-full transition-colors"
                      style={{
                        background: tier === t ? "#1FDD83" : "transparent",
                        color: tier === t ? "#04060a" : "var(--qt-text-2)",
                        fontWeight: 700, letterSpacing: "0.10em",
                      }}>
                {t === "standard" ? "STANDARD" : "DEEP DIVE"}
              </button>
            ))}
          </div>
        </div>

        {/* Iter270 — Subscription unlock chooser. Surfaces the layered-
            monetisation play directly in the configurator so subscribers
            can see their effective price before they talk to sales. */}
        {catalog.subscription_unlocks && (
          <div className="mt-5 flex flex-wrap items-center gap-2"
               data-testid="pqc-suite-subscription-row">
            <span className="qt-mono text-[10px]"
                  style={{ color: "var(--qt-text-3)", letterSpacing: "0.16em",
                           fontWeight: 700, marginRight: 4 }}>
              ALREADY A SUBSCRIBER?
            </span>
            <SubChip id="none"  label="No subscription" active={subscription === "none"}
                     onClick={() => { setAutoPicked(false); setSubscription("none"); }} />
            {catalog.subscription_unlocks.map((u) => (
              <SubChip key={u.id}
                       id={u.id}
                       label={`${u.label}${u.addl_pct > 0 ? `  ·  -${u.addl_pct}%` : ""}${u.all_inclusive ? "  ·  ALL-INCLUSIVE" : ""}`}
                       active={subscription === u.id}
                       featured={u.all_inclusive}
                       onClick={() => { setAutoPicked(false); setSubscription(u.id); }} />
            ))}
          </div>
        )}

        {/* Iter272 — "Detected from your account" banner when we
            auto-picked a paid tier. Lets the user see *why* the
            chooser is pre-selected + reassures them the price they
            see is the price they'll pay. */}
        {autoPicked && subscription !== "none" && (
          <div className="mt-3 inline-flex items-center gap-2 qt-mono text-[10px]"
               data-testid="pqc-suite-account-detected"
               style={{
                 color: "#1FDD83",
                 letterSpacing: "0.14em",
                 fontWeight: 700,
                 padding: "5px 10px",
                 borderRadius: 99,
                 background: "rgba(31,221,131,0.08)",
                 border: "1px solid rgba(31,221,131,0.32)",
               }}>
            <Sparkles size={11} />
            DETECTED · YOU'RE ON A QORTRACE{" "}
            {(catalog.subscription_unlocks.find((u) => u.id === subscription) || {})
                .label?.toUpperCase() || ""}{" "}
            SUBSCRIPTION · UNLOCK AUTO-APPLIED
          </div>
        )}

        {/* Configurator grid */}
        <div className="mt-7 grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr_0.85fr] gap-4">
          {catalog.audits.map((a) => {
            const Icon = ICONS[a.icon] || Atom;
            const selected = selectedSlugs.includes(a.slug);
            const price = a.prices_usd[tier];
            return (
              <button key={a.slug}
                      type="button"
                      onClick={() => toggleSlug(a.slug)}
                      data-testid={`pqc-suite-audit-${a.slug}`}
                      aria-pressed={selected}
                      className="text-left transition-all"
                      style={{
                        border: selected
                          ? "1px solid #1FDD83"
                          : "1px solid var(--qt-border)",
                        background: selected
                          ? "linear-gradient(135deg, rgba(31,221,131,0.10), transparent 70%)"
                          : "rgba(7,10,18,0.55)",
                        borderRadius: 10,
                        padding: "1.1rem 1.1rem",
                        position: "relative",
                        cursor: "pointer",
                        boxShadow: selected
                          ? "0 10px 28px -16px rgba(31,221,131,0.45)"
                          : "none",
                      }}>
                {/* Checkmark badge */}
                <div className="absolute top-3 right-3 rounded-full flex items-center justify-center"
                     style={{
                       width: 22, height: 22,
                       border: selected ? "1.5px solid #1FDD83" : "1.5px solid var(--qt-border)",
                       background: selected ? "#1FDD83" : "transparent",
                     }}>
                  {selected && <Check size={12} style={{ color: "#04060a" }} strokeWidth={3} />}
                </div>

                <div className="flex items-center gap-2">
                  <Icon size={18} style={{ color: "#1FDD83" }} />
                  <div className="qt-mono text-[10px]"
                       style={{ color: "#1FDD83", letterSpacing: "0.18em", fontWeight: 700 }}>
                    PQC · AUDIT
                  </div>
                </div>
                <div className="qt-display text-lg mt-2"
                     style={{ color: "#fff", lineHeight: 1.2 }}>
                  {a.title}
                </div>
                <p className="text-xs mt-1.5"
                   style={{ color: "var(--qt-text-2)", lineHeight: 1.45 }}>
                  {a.tagline}
                </p>
                <div className="mt-3 flex items-baseline gap-2">
                  <div className="qt-display text-2xl" style={{ color: "#fff" }}>
                    ${price.toLocaleString()}
                  </div>
                  <div className="qt-mono text-[10px]"
                       style={{ color: "var(--qt-text-3)", letterSpacing: "0.10em" }}>
                    {a.duration_weeks[tier][0]}–{a.duration_weeks[tier][1]}W
                  </div>
                </div>
              </button>
            );
          })}

          {/* Quote panel */}
          <div className="qt-card relative overflow-hidden"
               data-testid="pqc-suite-quote"
               style={{
                 border: "1px solid #1FDD83",
                 background: "linear-gradient(180deg, rgba(31,221,131,0.10), rgba(4,6,10,0.85))",
                 padding: "1.1rem",
                 display: "flex", flexDirection: "column",
               }}>
            <div className="qt-mono text-[10px]"
                 style={{ color: "#1FDD83", letterSpacing: "0.20em", fontWeight: 700 }}>
              SUITE QUOTE
            </div>

            {quote && quote.count > 0 ? (
              <>
                <div className="mt-2 text-xs"
                     style={{ color: "var(--qt-text-3)" }}>
                  {quote.count} audit{quote.count === 1 ? "" : "s"} · {quote.tier === "standard" ? "Standard" : "Deep Dive"}
                  {quote.subscription_label && quote.subscription !== "none" && (
                    <span style={{ color: "#1FDD83" }}>
                      {" · "}{quote.subscription_label} subscriber
                    </span>
                  )}
                </div>

                <div className="mt-3 space-y-1.5">
                  <Row label="Subtotal"
                       value={`$${quote.subtotal_usd.toLocaleString()}`}
                       dim />
                  {quote.volume_discount_pct > 0 && (
                    <Row label={`Bundle (-${quote.volume_discount_pct}%)`}
                         value={`-$${Math.round(quote.subtotal_usd * quote.volume_discount_pct / 100).toLocaleString()}`}
                         accent />
                  )}
                  {quote.subscription_discount_pct > 0 && (
                    <Row label={`${quote.subscription_label} unlock (-${quote.subscription_discount_pct}%)`}
                         value={`-$${Math.round(quote.subtotal_usd * quote.subscription_discount_pct / 100).toLocaleString()}`}
                         accent />
                  )}
                </div>

                <div className="mt-3 pt-3 border-t"
                     style={{ borderColor: "rgba(31,221,131,0.20)" }}>
                  {quote.all_inclusive ? (
                    <>
                      <div className="qt-display text-2xl"
                           data-testid="pqc-suite-total"
                           style={{ color: "#1FDD83", lineHeight: 1.05 }}>
                        Included
                      </div>
                      <div className="qt-mono text-[10px] mt-1"
                           style={{ color: "#1FDD83", letterSpacing: "0.10em", fontWeight: 700 }}>
                        IN ENTERPRISE · ATLAS CONTRACT
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="qt-display text-3xl"
                           data-testid="pqc-suite-total"
                           style={{ color: "#fff", lineHeight: 1 }}>
                        ${quote.total_usd.toLocaleString()}
                      </div>
                      <div className="qt-mono text-[10px] mt-1"
                           style={{ color: "var(--qt-text-3)", letterSpacing: "0.10em" }}>
                        TOTAL · {quote.currency.toUpperCase()}
                      </div>
                    </>
                  )}
                </div>

                <div className="mt-3 text-[11px]"
                     style={{ color: "var(--qt-text-2)", lineHeight: 1.4 }}>
                  {quote.savings_explanation}
                </div>

                {quote.matrix_pro_included && (
                  <div className="mt-2 qt-mono text-[10px]"
                       style={{ color: "#1FDD83", letterSpacing: "0.10em", fontWeight: 700 }}>
                    + MPC VENDOR MATRIX PRO INCLUDED · $5,000/YR VALUE
                  </div>
                )}

                <Link to={quoteUrl}
                      data-testid="pqc-suite-cta"
                      onClick={handleSuiteCheckout}
                      className="qt-btn qt-btn-primary mt-auto"
                      style={{
                        marginTop: 14,
                        fontSize: 13, padding: "0.7rem 1rem",
                        background: "#1FDD83",
                        color: "#04060a",
                        fontWeight: 700,
                        justifyContent: "center",
                        opacity: checkoutLoading ? 0.7 : 1,
                        pointerEvents: checkoutLoading ? "none" : "auto",
                      }}>
                  {checkoutLoading
                    ? "Opening Stripe…"
                    : (account && !quote.all_inclusive ? "Pay with Stripe" : "Get suite quote")}
                  {!checkoutLoading && <ArrowRight size={14} />}
                </Link>

                {checkoutError && (
                  <div className="mt-2 text-[11px]"
                       data-testid="pqc-suite-checkout-error"
                       style={{ color: "#FF8E8E", lineHeight: 1.4 }}>
                    {checkoutError}{" "}
                    <Link to={quoteUrl}
                          style={{ color: "#1FDD83", fontWeight: 700,
                                   textDecoration: "underline" }}>
                      Talk to sales →
                    </Link>
                  </div>
                )}
              </>
            ) : (
              <div className="text-xs mt-2"
                   style={{ color: "var(--qt-text-2)" }}>
                Select at least one audit to see the quote.
              </div>
            )}
          </div>
        </div>

        {/* Discount ladder strip */}
        <div className="mt-5 flex flex-wrap items-center gap-3 text-xs"
             style={{ color: "var(--qt-text-3)" }}
             data-testid="pqc-suite-discount-ladder">
          <span className="qt-mono"
                style={{ letterSpacing: "0.14em", fontWeight: 700, color: "var(--qt-text-2)" }}>
            DISCOUNT LADDER
          </span>
          <span>1 audit · full price</span>
          <span style={{ color: "rgba(255,255,255,0.18)" }}>·</span>
          <span>2 audits ·{" "}
            <b style={{ color: "#1FDD83" }}>15% off</b>
          </span>
          <span style={{ color: "rgba(255,255,255,0.18)" }}>·</span>
          <span>3 audits ·{" "}
            <b style={{ color: "#1FDD83" }}>25% off</b> (Full Suite)
          </span>
        </div>
      </div>
    </section>
  );
}


function Row({ label, value, dim, accent }) {
  return (
    <div className="flex items-baseline justify-between text-xs">
      <span style={{ color: dim ? "var(--qt-text-3)" : "var(--qt-text-2)" }}>{label}</span>
      <span className="qt-mono"
            style={{
              color: accent ? "#1FDD83" : dim ? "var(--qt-text-2)" : "#fff",
              fontWeight: accent ? 700 : 500,
            }}>
        {value}
      </span>
    </div>
  );
}


// Iter270 — Subscription chooser chip.
function SubChip({ id, label, active, featured, onClick }) {
  return (
    <button type="button"
            data-testid={`pqc-suite-sub-${id}`}
            onClick={onClick}
            aria-pressed={active}
            className="qt-mono text-[10px] transition-all"
            style={{
              padding: "5px 10px",
              borderRadius: 99,
              letterSpacing: "0.10em",
              fontWeight: 700,
              cursor: "pointer",
              border: active
                ? `1px solid ${featured ? "#00E5FF" : "#1FDD83"}`
                : "1px solid var(--qt-border)",
              background: active
                ? (featured ? "rgba(0,229,255,0.12)" : "rgba(31,221,131,0.10)")
                : "rgba(7,10,18,0.55)",
              color: active
                ? (featured ? "#00E5FF" : "#1FDD83")
                : "var(--qt-text-2)",
            }}>
      {label.toUpperCase()}
    </button>
  );
}
